/**
 * Which language the app is rendering in.
 *
 * Kept separate from the dictionaries and from React so that non-component code
 * — the API client, the formatters, `buildRallyPayload` — can read the active
 * locale without importing a hook. The React provider in `./index` is the only
 * thing that writes it.
 *
 * Default is `ko`. This is a private tool for a Korean alliance; English is the
 * fallback for the terms the game itself never translated, not the base case.
 */

export type Locale = 'ko' | 'en';

export const LOCALES: readonly Locale[] = ['ko', 'en'];

export const DEFAULT_LOCALE: Locale = 'ko';

const STORAGE_KEY = 'kso.locale';

function isLocale(value: unknown): value is Locale {
  return value === 'ko' || value === 'en';
}

/**
 * Reads the stored preference.
 *
 * Storage is unavailable in a Node test run and can throw in a browser with
 * site data blocked, so a failure here falls back to the default rather than
 * taking the app down over a preference.
 */
export function readStoredLocale(): Locale {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

export function storeLocale(locale: Locale): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, locale);
  } catch {
    // A preference that cannot be persisted is still worth honouring for this
    // session; there is nothing useful to report to the operator here.
  }
}
